(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const assignReveal = (selector, variant = 'fade-up', step = 90, start = 0) => {
    document.querySelectorAll(selector).forEach((element, index) => {
      element.dataset.suReveal = variant;
      element.style.setProperty('--su-delay', `${start + index * step}ms`);
    });
  };

  const configureReveals = () => {
    assignReveal('.su-hero-dark-left > *', 'fade-up', 85, 40);
    assignReveal('.su-hero-dark-right', 'scale', 0, 160);

    assignReveal('.su-proof-eyebrow, .su-proof-hero-col', 'fade-up', 100);
    assignReveal('.su-proof-side-col .su-side-block', 'fade-left', 110, 120);

    assignReveal('.su-why-header > *', 'fade-up', 80);
    assignReveal('.su-why-media-card', 'scale', 0, 100);
    assignReveal('.su-benefit-item', 'fade-up', 90, 80);

    assignReveal('.su-deploy-header > *', 'fade-up', 85);
    assignReveal('.su-deploy-card', 'fade-up', 130, 90);

    assignReveal('.su-platform-header > *', 'fade-up', 80);
    assignReveal('.su-flow-node, .su-flow-connector', 'fade-up', 95, 70);
    assignReveal('.su-platform-footer', 'fade-up', 0, 160);

    assignReveal('.su-retail-title-block > *', 'fade-up', 80);
    assignReveal('.su-gallery-controls', 'fade-left', 0, 120);
    assignReveal('.su-scene-card', 'fade-up', 90, 80);

    assignReveal('.su-global-ops-header > *', 'fade-up', 80);
    assignReveal('.su-ops-map-stage', 'scale', 0, 100);
  };

  const animateProofNumber = (element) => {
    if (!element || element.dataset.suCounted === 'true') return;
    const original = element.textContent.trim();
    const match = original.match(/([\d,]+)/);
    if (!match) return;

    const target = Number(match[1].replace(/,/g, ''));
    if (!Number.isFinite(target)) return;

    element.dataset.suCounted = 'true';
    if (reduceMotion) return;

    const duration = target > 1000 ? 1450 : 1050;
    const startedAt = performance.now();
    const formatter = new Intl.NumberFormat('en-US');

    const frame = (now) => {
      const progress = Math.min((now - startedAt) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 4);
      const current = Math.round(target * eased);
      element.textContent = original.replace(match[1], formatter.format(current));
      if (progress < 1) window.requestAnimationFrame(frame);
      else element.textContent = original;
    };

    window.requestAnimationFrame(frame);
  };

  const runJourneyOnce = () => {
    const steps = Array.from(document.querySelectorAll('.su-j-step'));
    const blocks = Array.from(document.querySelectorAll('.su-journey-visual-strip .su-jv-block'));
    const arrows = Array.from(document.querySelectorAll('.su-journey-visual-strip .su-jv-arrow'));
    const stage = document.querySelector('.su-why-media-card');
    if (!stage || stage.dataset.suJourneyPlayed === 'true' || reduceMotion) return;
    stage.dataset.suJourneyPlayed = 'true';

    steps.forEach((step) => step.classList.remove('active'));
    blocks.forEach((block) => block.classList.remove('is-su-current'));
    arrows.forEach((arrow) => arrow.classList.remove('is-su-current'));

    steps.forEach((step, index) => {
      window.setTimeout(() => {
        steps.forEach((item) => item.classList.remove('active'));
        blocks.forEach((item) => item.classList.remove('is-su-current'));
        arrows.forEach((item) => item.classList.remove('is-su-current'));
        step.classList.add('active');
        blocks[index]?.classList.add('is-su-current');
        if (index > 0) arrows[index - 1]?.classList.add('is-su-current');
      }, index * 820);
    });
  };

  /* "Dynamic Breathing" (2026-09-29): global section + card reveals.
     Tags every main-content section as a reveal unit, so homepage,
     product pages, technology and about pages are covered without
     editing each section file. Card groups get a slight stagger.
     Above-the-fold elements are shown immediately (no flash). */
  const configureGlobalReveals = () => {
    document.querySelectorAll('main .shopify-section').forEach((section) => {
      section.removeAttribute('data-su-reveal');
    });
    document.querySelectorAll('main .shopify-section').forEach((section) => {
      const cards = section.querySelectorAll(
        '.su-model-card, .su-rws__card, .su-ops-stat, .sop-flow__step, .su-deploy-card'
      );
      cards.forEach((card, index) => {
        if (card.dataset.suReveal) return;
        card.dataset.suReveal = 'fade-up';
        card.style.setProperty('--su-delay', (Math.min(index, 3) * 60) + 'ms');
      });
    });
    // Show above-the-fold reveals immediately to avoid a flash.
    document.querySelectorAll('[data-su-reveal]:not(.is-su-visible)').forEach((element) => {
      const rect = element.getBoundingClientRect();
      if (rect.top < window.innerHeight && rect.bottom > 0) {
        element.classList.add('is-su-visible');
      }
    });
  };

  const initObservers = () => {
    const revealElements = Array.from(document.querySelectorAll('[data-su-reveal]'));
    if (reduceMotion || !('IntersectionObserver' in window)) {
      revealElements.forEach((element) => element.classList.add('is-su-visible'));
      return;
    }

    const revealObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-su-visible');
        observer.unobserve(entry.target);
      });
    }, { threshold: 0, rootMargin: '0px 0px -32px 0px' });

    revealElements.forEach((element) => revealObserver.observe(element));

    const proof = document.querySelector('.su-proof-wrap');
    if (proof) {
      const proofObserver = new IntersectionObserver((entries, observer) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        animateProofNumber(document.querySelector('.su-proof-big-num .su-digits'));
        animateProofNumber(document.querySelector('.su-side-num'));
        observer.disconnect();
      }, { threshold: 0.35 });
      proofObserver.observe(proof);
    }

    const journey = document.querySelector('.su-why-media-card');
    if (journey) {
      const journeyObserver = new IntersectionObserver((entries, observer) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        runJourneyOnce();
        observer.disconnect();
      }, { threshold: 0.4 });
      journeyObserver.observe(journey);
    }
  };

  const init = () => {
    configureReveals();
    configureGlobalReveals();
    initObservers();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
